import { motion } from 'framer-motion';
import { FileText } from 'lucide-react';
import { FadeInUp } from '../components/animations';
import SEO from '../components/SEO';

const TermsPage = () => {
  return (
    <div className="pt-20 min-h-screen bg-[#fafaf9]" data-testid="terms-page">
      <SEO title="Terms & Conditions | Taiga Sourcing" description="Terms and conditions for Taiga Sourcing services." path="/terms" />
      <section className="py-32 bg-gradient-to-br from-[#0B2A57] to-[#4A5F78] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <FadeInUp>
            <FileText size={48} className="mx-auto mb-4" />
            <h1
              className="text-5xl sm:text-6xl font-bold tracking-tight leading-none mb-6"
              style={{ fontFamily: 'Cabinet Grotesk' }}
            >
              Terms & Conditions
            </h1>
            <p className="text-lg text-white/90 leading-relaxed max-w-3xl mx-auto">
              Last updated: January 2026
            </p>
          </FadeInUp>
        </div>
      </section>

      <section className="py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <FadeInUp>
            <div className="prose prose-lg max-w-none">
              <h2 className="text-2xl font-bold text-[#0B2A57] mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>
                1. Agreement to Terms
              </h2>
              <p className="text-[#1F2937] mb-6">
                By accessing and using the Taiga Sourcing website (taigasourcing.com), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these terms, please do not use our services.
              </p>

              <h2 className="text-2xl font-bold text-[#0B2A57] mb-4 mt-8" style={{ fontFamily: 'Cabinet Grotesk' }}>
                2. Services Description
              </h2>
              <p className="text-[#1F2937] mb-4">
                Taiga Sourcing provides multi-category product sourcing and manufacturing coordination services, including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-[#1F2937] mb-6 space-y-2">
                <li>Manufacturer identification and evaluation</li>
                <li>Product development and sampling</li>
                <li>Production coordination</li>
                <li>Quality inspection and follow-up</li>
                <li>Shipment coordination and export documentation</li>
              </ul>

              <h2 className="text-2xl font-bold text-[#0B2A57] mb-4 mt-8" style={{ fontFamily: 'Cabinet Grotesk' }}>
                3. Order Terms
              </h2>
              <p className="text-[#1F2937] mb-4">
                <strong>Minimum Order Quantity (MOQ):</strong> 200-10,000 pieces per style, subject to product category and factory capabilities.
              </p>
              <p className="text-[#1F2937] mb-4">
                <strong>Sample Lead Time:</strong> Typically 10 days from tech pack approval.
              </p>
              <p className="text-[#1F2937] mb-4">
                <strong>Production Lead Time:</strong> 40-50 days from order confirmation and sample approval.
              </p>
              <p className="text-[#1F2937] mb-6">
                All timelines are estimates and may vary based on order complexity, fabric availability, and factory schedules.
              </p>

              <h2 className="text-2xl font-bold text-[#0B2A57] mb-4 mt-8" style={{ fontFamily: 'Cabinet Grotesk' }}>
                4. Pricing and Payment
              </h2>
              <p className="text-[#1F2937] mb-4">
                Pricing is provided on a case-by-case basis depending on:
              </p>
              <ul className="list-disc pl-6 text-[#1F2937] mb-4 space-y-2">
                <li>Product specifications and complexity</li>
                <li>Order quantity</li>
                <li>Fabric and material requirements</li>
                <li>Delivery timeline</li>
              </ul>
              <p className="text-[#1F2937] mb-6">
                Payment terms are negotiated individually and typically include advance payment, milestone payments, and balance before shipment.
              </p>

              <h2 className="text-2xl font-bold text-[#0B2A57] mb-4 mt-8" style={{ fontFamily: 'Cabinet Grotesk' }}>
                5. Quality Standards
              </h2>
              <p className="text-[#1F2937] mb-4">
                We coordinate quality inspections at key production stages. Quality standards and acceptable levels are agreed upon with the buyer before production begins.
              </p>
              <p className="text-[#1F2937] mb-6">
                Quality issues must be reported within 7 days of delivery. Photographic evidence and detailed descriptions are required for any quality claims.
              </p>

              <h2 className="text-2xl font-bold text-[#0B2A57] mb-4 mt-8" style={{ fontFamily: 'Cabinet Grotesk' }}>
                6. Intellectual Property
              </h2>
              <p className="text-[#1F2937] mb-6">
                All designs, tech packs, and proprietary information shared with Taiga Sourcing remain the property of the client. We maintain strict confidentiality and will not reproduce or share designs without explicit permission.
              </p>

              <h2 className="text-2xl font-bold text-[#0B2A57] mb-4 mt-8" style={{ fontFamily: 'Cabinet Grotesk' }}>
                7. Cancellations and Refunds
              </h2>
              <p className="text-[#1F2937] mb-4">
                <strong>Sample Orders:</strong> May be cancelled before production begins. Advance payments are non-refundable once production starts.
              </p>
              <p className="text-[#1F2937] mb-6">
                <strong>Bulk Orders:</strong> Cancellation terms depend on production stage. Once fabric is purchased and production begins, orders cannot be cancelled without mutual agreement on compensation.
              </p>

              <h2 className="text-2xl font-bold text-[#0B2A57] mb-4 mt-8" style={{ fontFamily: 'Cabinet Grotesk' }}>
                8. Limitation of Liability
              </h2>
              <p className="text-[#1F2937] mb-6">
                Taiga Sourcing acts as an intermediary between clients and manufacturers. While we exercise due diligence in vendor selection and quality control, we are not liable for manufacturing defects beyond the scope of our quality inspection standards. Our liability is limited to the value of the specific order in question.
              </p>

              <h2 className="text-2xl font-bold text-[#0B2A57] mb-4 mt-8" style={{ fontFamily: 'Cabinet Grotesk' }}>
                9. Force Majeure
              </h2>
              <p className="text-[#1F2937] mb-6">
                Taiga Sourcing shall not be liable for delays or failures in performance resulting from acts beyond our reasonable control, including but not limited to natural disasters, war, terrorism, labor disputes, supplier failures, or government actions.
              </p>

              <h2 className="text-2xl font-bold text-[#0B2A57] mb-4 mt-8" style={{ fontFamily: 'Cabinet Grotesk' }}>
                10. Governing Law
              </h2>
              <p className="text-[#1F2937] mb-6">
                These terms shall be governed by and construed in accordance with the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Surat, Gujarat, India.
              </p>

              <h2 className="text-2xl font-bold text-[#0B2A57] mb-4 mt-8" style={{ fontFamily: 'Cabinet Grotesk' }}>
                11. Changes to Terms
              </h2>
              <p className="text-[#1F2937] mb-6">
                We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting to the website. Continued use of our services after changes constitutes acceptance of modified terms.
              </p>

              <h2 className="text-2xl font-bold text-[#0B2A57] mb-4 mt-8" style={{ fontFamily: 'Cabinet Grotesk' }}>
                12. Contact Information
              </h2>
              <p className="text-[#1F2937] mb-2">
                For questions about these Terms & Conditions, please contact:
              </p>
              <div className="bg-[#fafaf9] p-6 rounded-lg border border-[#0B2A57]/10 mb-6">
                <p className="text-[#1F2937]"><strong>Taiga Sourcing</strong></p>
                <p className="text-[#1F2937]">603-605, IFC Surana, VIP Road, Vesu</p>
                <p className="text-[#1F2937]">Surat, Gujarat 395007, India</p>
                <p className="text-[#1F2937] mt-2">Email: info@taigasourcing.com</p>
                <p className="text-[#1F2937]">Phone: +91 80999 75662</p>
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;